import { Empty, Spin, Table, Typography } from 'antd';
import { TrackedCard, useListTrackedCardsQuery, useListReturnableQuery } from '../../../redux/api/kit-staging';
import { OliCardTile } from '../../../components/KitStaging/OliCardTile';
import { groupByOli, OliGroup } from './groupByOli';

interface Props {
	view?: 'list' | 'cards';
	selectedGroup?: OliGroup | null;
	onSelect?: (g: OliGroup | null) => void;
}

export const ReturnableBoard = ({ view = 'cards', selectedGroup, onSelect }: Props) => {
	// The returnable endpoint returns per-card matches (Q4.7 4-scenario predicate).
	// Pair them back into OliGroups so the operator sees the same combined tile
	// pattern as everywhere else and can use the left sidebar for actions.
	const { data: returnableCards, isLoading } = useListReturnableQuery(undefined, {
		pollingInterval: 10000,
	});
	// Pull ALL active cards (including the partner if it isn't returnable) so the
	// OliCardTile can render kit + material together.
	const { data: allCards } = useListTrackedCardsQuery({}, { pollingInterval: 10000 });

	const returnableOlis = new Set((returnableCards ?? []).map((c) => c.order_line_item_id));
	const candidateCards: TrackedCard[] = [
		...(returnableCards ?? []),
		...(allCards ?? []).filter(
			(c) => returnableOlis.has(c.order_line_item_id) && !(returnableCards ?? []).some((r) => r.id === c.id),
		),
	];
	const groups = groupByOli(candidateCards).filter((g) => returnableOlis.has(g.oli));

	const handleSelect = (g: OliGroup) => {
		if (!onSelect) return;
		onSelect(selectedGroup?.oli === g.oli ? null : g);
	};

	return (
		<div style={{ padding: 16, width: '100%' }}>
			<Typography.Title level={3} style={{ margin: 0 }}>
				Return Kit{' '}
				<Typography.Text type="secondary" style={{ fontSize: 14 }}>
					({groups.length})
				</Typography.Text>
			</Typography.Title>

			{view === 'list' ? (
				<Table<OliGroup>
					rowKey="oli"
					loading={isLoading}
					dataSource={groups}
					pagination={false}
					onRow={(record) => ({ onClick: () => handleSelect(record) })}
					columns={[
						{
							title: 'OLI',
							key: 'oli',
							render: (_v: unknown, g: OliGroup) => g.job?.job_number || `OLI #${g.oli}`,
						},
						{ title: 'Part', key: 'part', render: (_v: unknown, g: OliGroup) => g.job?.part_name || '—' },
						{
							title: 'Kit status',
							key: 'kit_status',
							render: (_v: unknown, g: OliGroup) => g.kitCard?.status ?? '—',
						},
						{
							title: 'Material status',
							key: 'mat_status',
							render: (_v: unknown, g: OliGroup) => g.materialCard?.status ?? '—',
						},
					]}
				/>
			) : isLoading ? (
				<div style={{ padding: 48, textAlign: 'center' }}>
					<Spin />
				</div>
			) : groups.length === 0 ? (
				<Empty style={{ marginTop: 48 }} description="No returnable cards" />
			) : (
				<div
					style={{
						marginTop: 12,
						display: 'grid',
						gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
						gap: 8,
					}}
				>
					{groups.map((g) => (
						<div key={g.oli} style={{ flexShrink: 0 }}>
							<OliCardTile
								group={g}
								currentArea={
									// Render the tile from the card's actual area so the area
									// strip + "elsewhere" badges read correctly.
									g.kitCard?.current_area ?? g.materialCard?.current_area ?? 'jsa'
								}
								selected={selectedGroup?.oli === g.oli}
								onSelect={handleSelect}
							/>
						</div>
					))}
				</div>
			)}
		</div>
	);
};
